import {
  Button,
  CardContent,
  Grid,
  TextField,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import ArrowBackSharpIcon from "@mui/icons-material/ArrowBackSharp";
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HOST_URL from "../../../utils/Url";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import ToastApp from "../../../ToastApp";

type Props = {};

const GenderMasterAdd = (props: Props) => {
  let navigate = useNavigate();
  const { t } = useTranslation();
  const back = useNavigate();

  const requiredFields = ["genderName"];
  const [toaster, setToaster] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Enhanced validation schema with comprehensive input validation
  const validationSchema = Yup.object({
    genderName: Yup.string()
      .test(
        "required",
        t("text.reqGenderName"),
        function (value: any) {
          return value && value.trim() !== "";
        }
      )
      .max(30, t("text.genderNameMaxLength"))
      .matches(/^[a-zA-Z\s\-]+$/, t("text.invalidGenderName"))
      .test(
        "no-special-chars-start-end",
        t("text.noSpecialCharsStartEnd"),
        function (value: any) {
          if (!value) return true;
          return !/^[^a-zA-Z\s]|[^a-zA-Z\s]$/.test(value);
        }
      )
      .test(
        "no-multiple-special-chars",
        t("text.noMultipleSpecialChars"),
        function (value: any) {
          if (!value) return true;
          return !/[\-\s]{2,}/.test(value);
        }
      ),
    
    genderCode: Yup.string()
      .max(10, t("text.genderCodeMaxLength"))
      .matches(/^[a-zA-Z\-]*$/, t("text.invalidGenderCode"))
      .test(
        "no-special-chars-start-end-short",
        t("text.noSpecialCharsStartEndShort"),
        function (value: any) {
          if (!value) return true;
          return !/^[^a-zA-Z]|[^a-zA-Z]$/.test(value);
        }
      ),
  });

  // Function to sanitize input by removing unwanted characters
  const sanitizeInput = (value: string, fieldName: string) => {
    if (!value) return value;
    
    let sanitized = value;
    
    switch(fieldName) {
      case 'genderName':
        sanitized = value.replace(/[^a-zA-Z\s\-]/g, '');
        sanitized = sanitized.replace(/([\-\s]){2,}/g, '$1');
        sanitized = sanitized.replace(/^[^a-zA-Z\s]+|[^a-zA-Z\s]+$/g, '');
        break;
      case 'genderCode':
        sanitized = value.replace(/[^a-zA-Z\-]/g, '');
        sanitized = sanitized.replace(/^[^a-zA-Z]+|[^a-zA-Z]+$/g, '');
        break;
      default:
        sanitized = value.replace(/[<>{}[\]\\]/g, '');
    }
    
    return sanitized;
  };

  // Custom onChange handler to sanitize input in real-time
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value, name);
    
    if (value !== sanitizedValue) {
      formik.setFieldValue(name, sanitizedValue);
    } else {
      formik.handleChange(e);
    }
  };

  const formik = useFormik({
    initialValues: {
      genderID: -1,
      genderName: "",
      genderCode: ""
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const sanitizedValues = {
        ...values,
        genderName: sanitizeInput(values.genderName, 'genderName'),
        genderCode: sanitizeInput(values.genderCode, 'genderCode')
      };
      
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
      
      if (isSubmitting) {
        toast.info(t("text.requestInProgress"));
        return;
      }
      
      setIsSubmitting(true);
      
      try {
        const response = await axios.post(
          HOST_URL.HOST_URL + `Gender/AddUpdateGenderMaster`,
          sanitizedValues
        );
        
        if (response.data.isSuccess) {
          setToaster(false);
          toast.success(response.data.mesg);
          navigate("/master/GenderMaster");
        } else {
          setToaster(true);
          toast.error(response.data.mesg);
        }
      } catch (error: any) {
        if (error.response && error.response.status === 429) {
          toast.error(t("text.tooManyRequests"));
        } else {
          toast.error(t("text.somethingWentWrong"));
        }
        setToaster(true);
      } finally {
        submitTimeoutRef.current = setTimeout(() => {
          setIsSubmitting(false);
        }, 2000);
      }
    },
  });

  useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div>
      <div
        style={{
          padding: "-5px 5px",
          backgroundColor: "#ffffff",
          borderRadius: "5px",
          marginTop: "5px",
          border: ".5px solid #FF7722",
        }}
      >
        <CardContent>
          <Typography
            variant="h5"
            textAlign="center"
            style={{ fontSize: "18px", fontWeight: 500 }}
          >
            {t("text.CreateGenderMaster")}
          </Typography>

          <Grid item sm={4} xs={12}>
            <Typography style={{ marginTop: "-75px" }}>
              <Button
                type="button"
                onClick={() => back(-1)}
                variant="contained"
                style={{
                  marginBottom: 15,
                  marginTop: "45px",
                  backgroundColor: `var(--header-background)`,
                  width: 20,
                }}
                disabled={isSubmitting}
              >
                <ArrowBackSharpIcon />
              </Button>
            </Typography>
          </Grid>
          <Divider />
          <br />
          <form onSubmit={formik.handleSubmit}>
            {toaster===false ?"":<ToastApp/>}
            <Grid item xs={12} container spacing={2}>
              <Grid item lg={6} xs={12}>
                <TextField
                  label={
                    <span>
                      {t("text.EnterGenderName")}{" "}
                      {requiredFields.includes("genderName") && (
                        <span
                          style={{
                            color: formik.values.genderName ? "green" : "red",
                          }}
                        >
                          *
                        </span>
                      )}
                    </span>
                  }
                  value={formik.values.genderName}
                  placeholder={t("text.EnterGenderName")}
                  size="small"
                  name="genderName"
                  id="genderName"
                  fullWidth
                  style={{ backgroundColor: "white" }}
                  onChange={handleInputChange}
                  onBlur={formik.handleBlur}
                  disabled={isSubmitting}
                  inputProps={{ 
                    maxLength: 30,
                    pattern: "[a-zA-Z\\s\\-]*"
                  }}
                  error={formik.touched.genderName && Boolean(formik.errors.genderName)}
                />
                {formik.touched.genderName && formik.errors.genderName ? (
                  <div style={{ color: "red", margin: "5px" }}>
                    {String(formik.errors.genderName)}
                  </div>
                ) : null}
              </Grid>

              <Grid item lg={6} xs={12}>
                <TextField
                  label={t("text.EnterGenderCode")}
                  value={formik.values.genderCode}
                  name="genderCode"
                  id="genderCode"
                  placeholder={t("text.EnterGenderCode")}
                  size="small"
                  fullWidth
                  style={{ backgroundColor: "white" }}
                  onChange={handleInputChange}
                  onBlur={formik.handleBlur}
                  disabled={isSubmitting}
                  inputProps={{ 
                    maxLength: 10,
                    pattern: "[a-zA-Z\\-]*"
                  }}
                  error={formik.touched.genderCode && Boolean(formik.errors.genderCode)}
                />
                {formik.touched.genderCode && formik.errors.genderCode ? (
                  <div style={{ color: "red", margin: "5px" }}>
                    {String(formik.errors.genderCode)}
                  </div>
                ) : null}
              </Grid>

              <Grid item lg={6} sm={6} xs={12}>
                <Grid>
                  <Button
                    type="submit"
                    fullWidth
                    style={{
                      backgroundColor: `var(--header-background)`,
                      color: "white",
                      marginTop: "10px",
                    }}
                    disabled={isSubmitting || !formik.isValid}
                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {isSubmitting ? t("text.submitting") : t("text.save")}
                  </Button>
                </Grid>
              </Grid>
              <Grid item lg={6} sm={6} xs={12}>
                <Button
                  type="reset"
                  fullWidth
                  style={{
                    backgroundColor: "#F43F5E",
                    color: "white",
                    marginTop: "10px",
                  }}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    formik.resetForm();
                  }}
                  disabled={isSubmitting}
                >
                  {t("text.reset")}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </div>
    </div>
  );
};

export default GenderMasterAdd;